import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type LeaderboardRow } from '../../api/client';
import { useLocale } from '../../i18n/LocaleContext';

type GameFilter = 'solo_timed' | 'solo_lives' | 'solo_timebank' | 'solo_daily' | 'mp_timed';
type SortFilter = 'score' | 'streak';
type ScopeFilter = 'all' | 'weekly' | 'daily';
type ViewFilter = 'runs' | 'career';

function cycle<T>(list: readonly T[], current: T): T {
  const i = list.indexOf(current);
  return list[(i + 1) % list.length]!;
}

export function LeaderboardPage() {
  const { t } = useLocale();
  const [params] = useSearchParams();
  const initialGame = (params.get('game') as GameFilter | null) ?? 'solo_timed';
  const [view, setView] = useState<ViewFilter>('runs');
  const [game, setGame] = useState<GameFilter>(
    ['solo_timed', 'solo_lives', 'solo_timebank', 'solo_daily', 'mp_timed'].includes(initialGame)
      ? initialGame
      : 'solo_timed',
  );
  const [sort, setSort] = useState<SortFilter>('score');
  const [scope, setScope] = useState<ScopeFilter>(
    initialGame === 'solo_daily' ? 'daily' : 'all',
  );
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [durations, setDurations] = useState<number[]>([]);
  const [careerKind, setCareerKind] = useState<'rating' | 'wins'>('rating');
  const [rows, setRows] = useState<LeaderboardRow[]>([]);

  const games = useMemo(
    () =>
      [
        { id: 'solo_timed' as const, label: t('soloTimed') },
        { id: 'solo_lives' as const, label: t('soloLives') },
        { id: 'solo_timebank' as const, label: t('soloTimeBank') },
        { id: 'solo_daily' as const, label: t('lbDaily') },
        { id: 'mp_timed' as const, label: t('lbMultiplayer') },
      ] as const,
    [t],
  );

  const showDuration = game === 'solo_timed' || game === 'mp_timed';
  const durationOptions = useMemo(() => [null, ...durations] as Array<number | null>, [durations]);

  useEffect(() => {
    if (game === 'solo_lives') setSort('streak');
    else setSort('score');
    setDurationSec(null);
    setScope(game === 'solo_daily' ? 'daily' : 'all');
  }, [game]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (view === 'career') {
        const r = await api.leaderboard({ view: 'career', kind: careerKind });
        if (cancelled) return;
        setRows(r.rows);
        setDurations([]);
        return;
      }
      const r = await api.leaderboard({
        view: 'runs',
        game,
        sort,
        scope,
        durationSec: durationSec ?? undefined,
      });
      if (cancelled) return;
      setRows(r.rows);
      setDurations(r.durations ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [view, game, sort, scope, durationSec, careerKind]);

  const durationLabel =
    durationSec == null ? t('lbAnyDuration') : `${durationSec}${t('soloSec')}`;
  const scopeLabel =
    scope === 'daily' ? t('lbToday') : scope === 'weekly' ? t('lbWeekly') : t('lbAllTime');

  const cycleScope = () => {
    if (game === 'solo_daily') {
      setScope('daily');
      return;
    }
    setScope(scope === 'all' ? 'weekly' : 'all');
  };

  return (
    <section className="panel">
      <h1>{t('lbTitle')}</h1>

      {view === 'runs' ? (
        <div className="lb-games" role="tablist" aria-label={t('lbGameType')}>
          {games.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={game === g.id}
              className={`lb-game${game === g.id ? ' active' : ''}`}
              onClick={() => setGame(g.id)}
            >
              {g.label}
            </button>
          ))}
        </div>
      ) : null}

      <table className="table lb-table">
        <thead>
          {view === 'runs' ? (
            <tr>
              <th>
                <button
                  type="button"
                  className="th-filter"
                  onClick={() => setView('career')}
                  title={t('lbCareer')}
                >
                  #
                  <span className="th-sub">{t('lbPlays')}</span>
                </button>
              </th>
              <th>{t('lbPlayer')}</th>
              <th>
                <button
                  type="button"
                  className={`th-filter${sort === 'score' ? ' active' : ''}`}
                  onClick={() => setSort('score')}
                >
                  {t('lbScore')}
                  {sort === 'score' ? (
                    <span className="th-caret" aria-hidden>
                      ▾
                    </span>
                  ) : null}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`th-filter${sort === 'streak' ? ' active' : ''}`}
                  onClick={() => setSort('streak')}
                >
                  {t('lbStreak')}
                  {sort === 'streak' ? (
                    <span className="th-caret" aria-hidden>
                      ▾
                    </span>
                  ) : null}
                </button>
              </th>
              {showDuration ? (
                <th>
                  <button
                    type="button"
                    className="th-filter active"
                    onClick={() => setDurationSec(cycle(durationOptions, durationSec))}
                    title={t('lbDuration')}
                  >
                    {t('lbDuration')}
                    <span className="th-sub">{durationLabel}</span>
                  </button>
                </th>
              ) : null}
              <th>
                <button
                  type="button"
                  className="th-filter active"
                  onClick={cycleScope}
                >
                  {t('lbWhen')}
                  <span className="th-sub">{scopeLabel}</span>
                </button>
              </th>
            </tr>
          ) : (
            <tr>
              <th>
                <button
                  type="button"
                  className="th-filter"
                  onClick={() => setView('runs')}
                  title={t('lbPlays')}
                >
                  #
                  <span className="th-sub">{t('lbCareer')}</span>
                </button>
              </th>
              <th>{t('lbPlayer')}</th>
              <th>
                <button
                  type="button"
                  className={`th-filter${careerKind === 'rating' ? ' active' : ''}`}
                  onClick={() => setCareerKind('rating')}
                >
                  {t('lbRating')}
                  {careerKind === 'rating' ? (
                    <span className="th-caret" aria-hidden>
                      ▾
                    </span>
                  ) : null}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`th-filter${careerKind === 'wins' ? ' active' : ''}`}
                  onClick={() => setCareerKind('wins')}
                >
                  {t('lbWins')}
                  {careerKind === 'wins' ? (
                    <span className="th-caret" aria-hidden>
                      ▾
                    </span>
                  ) : null}
                </button>
              </th>
              <th>{t('lbStreak')}</th>
              <th>W%</th>
            </tr>
          )}
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="muted">
                {t('lbEmpty')}
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={r.id}>
                <td>{i + 1}</td>
                <td>{r.username}</td>
                {view === 'runs' ? (
                  <>
                    <td>{r.score ?? r.bestTimedScore}</td>
                    <td>{r.bestStreak}</td>
                    {showDuration ? (
                      <td>{r.durationSec != null ? `${r.durationSec}${t('soloSec')}` : '—'}</td>
                    ) : null}
                    <td className="muted">
                      {r.date
                        ? new Date(r.date).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                  </>
                ) : (
                  <>
                    <td>{r.rating}</td>
                    <td>{r.wins ?? '—'}</td>
                    <td>{r.bestStreak}</td>
                    <td>{r.winRate}%</td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
